import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Settings as SettingsIcon,
  Palette,
  Clock,
  CreditCard,
  Briefcase,
  Users as UsersIcon,
  ShieldHalf,
  BookOpen,
  Quote,
  Globe,
  Mail,
  Shield,
  Building2,
  Ticket,
  Puzzle,
  BookUser,
  Boxes,
  FolderOpen,
  FileCheck,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { PageLayout } from '@/components/ui/PageLayout';
import { Select } from '@/components/ui/Select';
import { TYPE, UI_SECONDARY } from '@/lib/typography';
import { useAuth } from '@/lib/auth';
import { useActiveClinic } from '@/lib/activeClinic';
import { INTEGRATIONS_PERMISSION_RESOURCES, type PermissionResource } from '@/lib/permissions';
import { showSubClinicsNavItem } from '@/lib/subClinicSettingsAccess';

const INTEGRATION_PATH_PREFIXES = [
  '/settings/integrations',
  '/settings/payments',
  '/settings/questionnaires',
  '/settings/communicate',
  '/settings/sms',
  '/settings/sygeforsikring',
  '/settings/whereby',
  '/settings/patient-reports',
];

interface SubNavItem {
  to: string;
  i18nKey: string;
  icon: typeof SettingsIcon;
  permissionResource?: PermissionResource;
}

interface SubNavGroup {
  i18nKey: string;
  items: SubNavItem[];
}

const SUB_NAV_GROUPS: SubNavGroup[] = [
  {
    i18nKey: 'settings.nav_groups.clinic',
    items: [
      {
        to: '/settings/general',
        i18nKey: 'settings.nav.general',
        icon: SettingsIcon,
        permissionResource: 'settings_general',
      },
      {
        to: '/settings/branding',
        i18nKey: 'settings.nav.branding',
        icon: Palette,
        permissionResource: 'settings_general',
      },
      {
        to: '/settings/hours',
        i18nKey: 'settings.nav.hours',
        icon: Clock,
        permissionResource: 'settings_general',
      },
      {
        to: '/settings/services',
        i18nKey: 'settings.nav.services',
        icon: Briefcase,
        permissionResource: 'settings_general',
      },
      {
        to: '/settings/resources',
        i18nKey: 'settings.nav.resources',
        icon: Boxes,
        permissionResource: 'settings_general',
      },
      {
        to: '/settings/online-booking',
        i18nKey: 'settings.nav.online_booking',
        icon: Globe,
        permissionResource: 'online_booking',
      },
      {
        to: '/settings/email',
        i18nKey: 'settings.nav.email',
        icon: Mail,
        permissionResource: 'settings_general',
      },
      {
        to: '/settings/consent-texts',
        i18nKey: 'settings.nav.consent_texts',
        icon: FileCheck,
        permissionResource: 'settings_general',
      },
    ],
  },
  {
    i18nKey: 'settings.nav_groups.staff',
    items: [
      {
        to: '/settings/users',
        i18nKey: 'settings.nav.users',
        icon: UsersIcon,
        permissionResource: 'settings_users',
      },
      {
        to: '/settings/roles',
        i18nKey: 'settings.nav.roles',
        icon: ShieldHalf,
        permissionResource: 'settings_roles',
      },
    ],
  },
  {
    i18nKey: 'settings.nav_groups.finance',
    items: [
      {
        to: '/settings/billing',
        i18nKey: 'settings.nav.billing',
        icon: CreditCard,
        permissionResource: 'settings_billing',
      },
      {
        to: '/settings/punch-card-templates',
        i18nKey: 'settings.nav.punch_card_templates',
        icon: Ticket,
        permissionResource: 'punch_cards',
      },
    ],
  },
  {
    i18nKey: 'settings.nav_groups.clinical',
    items: [
      {
        to: '/settings/journal-templates',
        i18nKey: 'settings.nav.journal_templates',
        icon: BookOpen,
        permissionResource: 'journal',
      },
      {
        to: '/settings/journal-phrases',
        i18nKey: 'settings.nav.journal_phrases',
        icon: Quote,
        permissionResource: 'journal_phrases',
      },
      {
        to: '/settings/contacts',
        i18nKey: 'settings.nav.contacts',
        icon: BookUser,
        permissionResource: 'patients',
      },
      {
        to: '/settings/file-categories',
        i18nKey: 'settings.nav.file_categories',
        icon: FolderOpen,
        permissionResource: 'documents',
      },
    ],
  },
  {
    i18nKey: 'settings.nav_groups.organization',
    items: [
      {
        to: '/settings/integrations',
        i18nKey: 'settings.nav.integrations',
        icon: Puzzle,
        permissionResource: 'settings_general',
      },
      {
        to: '/settings/clinics',
        i18nKey: 'settings.nav.clinics',
        icon: Building2,
        permissionResource: 'multi_clinic',
      },
      {
        to: '/settings/audit-logs',
        i18nKey: 'settings.nav.audit_logs',
        icon: Shield,
        permissionResource: 'audit_logs',
      },
    ],
  },
];

function isIntegrationsPath(pathname: string) {
  return INTEGRATION_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function navItemActive(pathname: string, to: string) {
  if (to === '/settings/integrations') return isIntegrationsPath(pathname);
  return pathname === to || pathname.startsWith(`${to}/`);
}

export function SettingsLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { activeClinicId, activeClinic } = useActiveClinic();

  const visibleGroups = SUB_NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      if (!activeClinicId) return false;
      if (item.to === '/settings/integrations') {
        return INTEGRATIONS_PERMISSION_RESOURCES.some((r) =>
          hasPermission(activeClinicId, r, 'view'),
        );
      }
      if (item.to === '/settings/email') {
        return (
          hasPermission(activeClinicId, 'settings_general', 'view') ||
          hasPermission(activeClinicId, 'communication', 'view')
        );
      }
      if (item.to === '/settings/clinics') {
        return showSubClinicsNavItem({
          activeClinicId,
          activeClinicResolved: activeClinic != null,
          isSubClinic: activeClinic?.isSubClinic === true,
          canView: hasPermission(activeClinicId, 'multi_clinic', 'view'),
        });
      }
      if (!item.permissionResource) return true;
      return hasPermission(activeClinicId, item.permissionResource, 'view');
    }),
  })).filter((group) => group.items.length > 0);

  const flatItems = visibleGroups.flatMap((group) =>
    group.items.map((item) => ({
      ...item,
      groupKey: group.i18nKey,
      selectLabel: `${t(group.i18nKey)} · ${t(item.i18nKey)}`,
    })),
  );

  const selectValue =
    flatItems.find((item) => navItemActive(location.pathname, item.to))?.to ??
    flatItems[0]?.to ??
    '';

  const clinicKind = activeClinic?.isSubClinic
    ? t('settings.clinic_kind.sub')
    : t('settings.clinic_kind.main');
  const subtitle = activeClinic
    ? `${activeClinic.name} · ${clinicKind}`
    : t('settings.subtitle');

  return (
    <PageLayout title={t('settings.title')} subtitle={subtitle}>
      <div className="grid min-w-0 grid-cols-1 items-start gap-s4 nav:grid-cols-[240px_minmax(0,1fr)] nav:gap-s6">
        <div className="min-w-0 nav:hidden">
          {flatItems.length > 0 ? (
            <Select
              id="settings-section"
              label={t('settings.section')}
              value={selectValue}
              onChange={(e) => {
                if (e.target.value) navigate(e.target.value);
              }}
            >
              {flatItems.map((item) => (
                <option key={item.to} value={item.to}>
                  {item.selectLabel}
                </option>
              ))}
            </Select>
          ) : null}
        </div>

        <nav
          className="hidden min-w-0 flex-col gap-0.5 nav:flex"
          aria-label={t('settings.title')}
        >
          {visibleGroups.map((group) => (
            <div key={group.i18nKey}>
              <h2 className={cn(UI_SECONDARY, 'px-s3 pb-s1 pt-s4 font-semibold first:pt-0')}>
                {t(group.i18nKey)}
              </h2>
              {group.items.map(({ to, i18nKey, icon: Icon }) => {
                const active = navItemActive(location.pathname, to);
                return (
                  <NavLink
                    key={to}
                    to={to}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      TYPE.control,
                      'flex h-9 items-center gap-2.5 rounded-ctl px-s3 font-medium transition-colors',
                      active
                        ? 'bg-ds-teal-tint font-semibold text-ds-teal'
                        : 'text-ds-text hover:bg-ds-neutral-tint',
                    )}
                  >
                    <Icon
                      className={cn('h-4 w-4', active ? 'text-ds-teal' : 'text-ds-muted')}
                      aria-hidden
                    />
                    {t(i18nKey)}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </PageLayout>
  );
}
